/**
 * ParticipantGrid - Displays call participants in a grid layout
 * Supports video tracks, speaking indicators, hand raises, and participant actions
 */

'use client';

import React from 'react';
import { 
  User, 
  Mic, 
  MicOff, 
  Video, 
  VideoOff,
  Hand, 
  MoreVertical, 
  UserMinus,
  MonitorUp 
} from 'lucide-react';
import type { ParticipantGridProps, CallParticipant } from '../types';

interface ParticipantCardProps {
  participant: CallParticipant;
  videoRef?: HTMLVideoElement | null;
  showMenu?: boolean;
  isMenuOpen?: boolean;
  onMenuToggle?: () => void;
  onRequestMute?: () => void;
  onRemove?: () => void;
  isLocal?: boolean;
  className?: string;
}

export function ParticipantCard({
  participant,
  videoRef,
  showMenu = false,
  isMenuOpen = false,
  onMenuToggle,
  onRequestMute,
  onRemove,
  isLocal = false,
  className = '',
}: ParticipantCardProps) {
  return (
    <div 
      className={`relative flex items-center gap-3 p-2 rounded-lg transition-colors ${
        participant.isSpeaking 
          ? 'bg-green-900/30 ring-2 ring-green-500/50' 
          : 'bg-gray-800/30 hover:bg-gray-800/50'
      } ${className}`}
    >
      {/* Avatar */}
      <div className="relative">
        <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center overflow-hidden">
          {participant.avatar_url ? (
            <img 
              src={participant.avatar_url} 
              alt="" 
              className="w-full h-full object-cover" 
            />
          ) : (
            <span className="text-white font-medium text-sm">
              {participant.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </span>
          )}
        </div>
        
        {/* Speaking/Mute indicator */}
        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-gray-900 flex items-center justify-center">
          {participant.isMuted ? (
            <MicOff className="w-2.5 h-2.5 text-red-400" />
          ) : participant.isSpeaking ? (
            <div className="w-2.5 h-2.5 rounded-full bg-green-400 animate-pulse" />
          ) : (
            <Mic className="w-2.5 h-2.5 text-green-400" />
          )}
        </div>
      </div>
      
      {/* Name and status */}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">
          {participant.name}
          {isLocal && ' (You)'}
        </p>
        <div className="flex items-center gap-2">
          {participant.hasVideo && <Video className="w-3 h-3 text-blue-400" />}
          {participant.isScreenSharing && <MonitorUp className="w-3 h-3 text-green-400" />}
          {participant.isSpeaking && (
            <span className="text-green-400 text-xs">Speaking</span>
          )}
        </div>
      </div>
      
      {/* Hand raised indicator */}
      {participant.isHandRaised && (
        <Hand className="w-5 h-5 text-yellow-400 animate-bounce" />
      )}
      
      {/* Menu button (not for local participant) */}
      {showMenu && !isLocal && (
        <div className="relative">
          <button
            onClick={onMenuToggle}
            className="p-1.5 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <MoreVertical className="w-4 h-4 text-gray-400" />
          </button>
          
          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 top-full mt-1 w-44 bg-gray-800 rounded-lg shadow-xl border border-gray-700 py-1 z-30">
              {!participant.isMuted && onRequestMute && (
                <button
                  onClick={onRequestMute}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                >
                  <MicOff className="w-4 h-4" />
                  <span>Ask to Mute</span>
                </button>
              )}
              {onRemove && (
                <button
                  onClick={onRemove}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-400 hover:bg-gray-700 transition-colors"
                >
                  <UserMinus className="w-4 h-4" />
                  <span>Remove from Call</span>
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ParticipantGrid({
  participants,
  localParticipant,
  remoteVideoRefs,
  localVideoRef,
  showParticipantMenu = false,
  selectedParticipantMenu = null,
  onSelectParticipant,
  onRequestMute,
  onRemoveParticipant,
  participantHandRaises,
  className = '',
}: ParticipantGridProps) {
  // Create local participant object for display
  const localParticipantData: CallParticipant | null = localParticipant ? {
    identity: 'local',
    name: localParticipant.name,
    userId: 0,
    avatar_url: localParticipant.avatarUrl,
    isMuted: localParticipant.isMuted,
    isSpeaking: false,
    hasVideo: localParticipant.isVideoEnabled,
    isHandRaised: localParticipant.isHandRaised,
  } : null;
  
  // Merge hand raise state from participantHandRaises map
  const participantsWithHandRaises = participants.map(p => ({
    ...p,
    isHandRaised: p.isHandRaised || participantHandRaises?.get(p.userId) || false,
  }));
  
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      {/* Local participant first */}
      {localParticipantData && (
        <ParticipantCard
          participant={localParticipantData}
          isLocal
          className="bg-gray-800/50"
        />
      )}
      
      {/* Remote participants */}
      {participantsWithHandRaises.map(participant => (
        <ParticipantCard
          key={participant.identity}
          participant={participant}
          videoRef={remoteVideoRefs.get(participant.identity)}
          showMenu={showParticipantMenu}
          isMenuOpen={selectedParticipantMenu === participant.identity}
          onMenuToggle={() => onSelectParticipant?.(
            selectedParticipantMenu === participant.identity ? null : participant.identity
          )}
          onRequestMute={() => onRequestMute?.(participant.identity, participant.name)}
          onRemove={() => onRemoveParticipant?.(participant.identity, participant.name)}
        />
      ))}
    </div>
  );
}

/**
 * Compact participant list for small spaces
 */
export function ParticipantList({
  participants,
  maxVisible = 5,
  className = '',
}: {
  participants: CallParticipant[];
  maxVisible?: number;
  className?: string;
}) {
  const visibleParticipants = participants.slice(0, maxVisible);
  const remainingCount = participants.length - maxVisible;
  
  return (
    <div className={`flex items-center ${className}`}>
      <div className="flex -space-x-2">
        {visibleParticipants.map(p => (
          <div 
            key={p.identity}
            className="w-8 h-8 rounded-full bg-gray-600 border-2 border-gray-900 flex items-center justify-center overflow-hidden"
            title={p.name}
          >
            {p.avatar_url ? (
              <img src={p.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <span className="text-white text-xs font-medium">
                {p.name[0]?.toUpperCase()}
              </span>
            )}
          </div>
        ))}
        
        {remainingCount > 0 && (
          <div className="w-8 h-8 rounded-full bg-gray-700 border-2 border-gray-900 flex items-center justify-center">
            <span className="text-white text-xs font-medium">+{remainingCount}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default ParticipantGrid;
